import { useEffect, useRef, useState } from 'react';

/**
 * InvoicePreviewFrame — renders an invoice HTML document (from invoiceTemplates)
 * inside an iframe scaled to fit its container width.
 *
 * Two sizing modes:
 *  • Default: the invoice is authored at a fixed design width; we measure the
 *    container, apply a CSS transform so it always fits, then read the rendered
 *    height back so the wrapper collapses to the exact content height.
 *  • Paper mode (`paper={w,h}`): the frame renders at the paper's pixel width and
 *    is at least one full page tall (`max(paperHeight, contentHeight)`), so the
 *    preview is a true-to-size sheet — a one-item invoice still shows a full page,
 *    and a long invoice grows past it. This makes the preview match the printout.
 *
 * To avoid the flicker/"shake" you'd get from re-`srcDoc`-ing on every keystroke,
 * the HTML is (a) debounced and (b) written into the SAME iframe document via
 * document.write — the element is never remounted, so there is no blank flash.
 */
const DESIGN_W = 640;

export default function InvoicePreviewFrame({ html, className = '', debounceMs = 250, paper = null, reloadKey, fitHeight = 0 }) {
    const designW = paper?.w || DESIGN_W;
    const minH = paper?.h || 0;

    const wrapRef = useRef(null);
    const iframeRef = useRef(null);
    const [scale, setScale] = useState(0.5);
    const [wrapW, setWrapW] = useState(0);
    const [contentH, setContentH] = useState(minH || 600);
    const [debounced, setDebounced] = useState(html);
    // While the template/paper (reloadKey) changes, the iframe re-lays-out an entirely
    // new design — showing a half-drawn frame for a moment. Cover it with a skeleton
    // until the new layout settles so the switch feels smooth (text edits, which keep
    // the same layout, don't toggle this).
    const [settling, setSettling] = useState(false);
    useEffect(() => {
        if (reloadKey == null) return;
        setSettling(true);
        const id = setTimeout(() => setSettling(false), Math.max(debounceMs + 320, 520));
        return () => clearTimeout(id);
    }, [reloadKey, debounceMs]);

    // Debounce incoming HTML so fast typing doesn't thrash the iframe.
    useEffect(() => {
        const id = setTimeout(() => setDebounced(html), debounceMs);
        return () => clearTimeout(id);
    }, [html, debounceMs]);

    // Scale the design width to the container width. In fit-height mode
    // (`fitHeight` set — used by the theme thumbnails) the WHOLE page must stay
    // visible, so the scale also honours the fixed height and the sheet is
    // centred horizontally in whatever space is left.
    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const update = () => {
            setWrapW(el.clientWidth);
            const byWidth = el.clientWidth / designW;
            setScale(fitHeight ? Math.min(byWidth, fitHeight / Math.max(1, contentH)) : byWidth);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [designW, fitHeight, contentH]);

    // Write the HTML into the existing iframe document (no remount → no flash),
    // then measure the rendered height (twice, to catch late font layout). In paper
    // mode the height is clamped to at least one full page so short invoices still
    // render as a complete sheet.
    useEffect(() => {
        const f = iframeRef.current;
        const doc = f?.contentDocument || f?.contentWindow?.document;
        if (!doc) return;
        doc.open();
        doc.write(debounced);
        doc.close();
        const measure = () => {
            const measured = doc.body?.scrollHeight || 0;
            const h = Math.max(minH, measured);
            if (h) setContentH((prev) => (Math.abs(prev - (h + 4)) > 1 ? h + 4 : prev));
        };
        const t1 = setTimeout(measure, 30);
        const t2 = setTimeout(measure, 250);
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, [debounced, minH]);

    // Fit-height mode keeps the wrapper at the fixed height and centres the
    // scaled-down sheet; default mode collapses the wrapper to the content.
    const offsetX = fitHeight ? Math.max(0, (wrapW - designW * scale) / 2) : 0;
    return (
        <div ref={wrapRef} className={`relative w-full overflow-hidden ${className}`} style={{ height: fitHeight || contentH * scale }}>
            <iframe
                ref={iframeRef}
                title="invoice-preview"
                scrolling="no"
                style={{
                    width: designW,
                    height: contentH,
                    border: 0,
                    transform: `translateX(${offsetX}px) scale(${scale})`,
                    transformOrigin: 'top left',
                    pointerEvents: 'none',
                    background: '#fff',
                    opacity: settling ? 0 : 1,
                    transition: 'opacity .18s ease',
                }}
            />
            {settling && <PreviewSkeleton />}
        </div>
    );
}

// Invoice-shaped shimmer shown over the frame while a new theme lays out.
function PreviewSkeleton() {
    const bar = 'rounded bg-zinc-200/80 dark:bg-zinc-700/60 animate-pulse';
    const faint = 'rounded bg-zinc-100 dark:bg-zinc-800/60 animate-pulse';
    return (
        <div className="absolute inset-0 bg-white dark:bg-zinc-900 p-[6%] flex flex-col">
            <div className="flex justify-between gap-4">
                <div className="space-y-2">
                    <div className={`h-6 w-32 ${bar}`} />
                    <div className={`h-3 w-24 ${faint}`} />
                </div>
                <div className="flex flex-col items-end space-y-2">
                    <div className={`h-5 w-24 ${bar}`} />
                    <div className={`h-3 w-32 ${faint}`} />
                    <div className={`h-3 w-28 ${faint}`} />
                </div>
            </div>
            <div className="h-px w-full bg-zinc-200 dark:bg-zinc-700 my-5" />
            <div className="flex justify-between mb-4">
                <div className={`h-3 w-20 ${faint}`} />
                <div className={`h-3 w-40 ${faint}`} />
            </div>
            <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3">
                        <div className={`h-4 w-5 ${faint}`} />
                        <div className={`h-4 flex-1 ${faint}`} />
                        <div className={`h-4 w-14 ${faint}`} />
                    </div>
                ))}
            </div>
            <div className="mt-auto ml-auto w-1/2 space-y-2">
                <div className={`h-3 w-full ${faint}`} />
                <div className={`h-8 w-full ${bar}`} />
            </div>
        </div>
    );
}
